// src/pages/Dealers.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { Copy, Check, RefreshCw, Tag, CheckCircle, Clock, X, AlertTriangle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { dealer as dealerApi } from '../services/api';

const RECALL_WINDOW = 300; // 5 minutes in seconds

function StatCard({ icon: Icon, label, value, color }) {
  const theme = useTheme();
  return (
    <div className="rounded-2xl p-4"
      style={{
        backgroundColor: theme.card,
        boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)',
      }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: theme.textMuted }}>{label}</p>
      </div>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

// Confirm modal
function ConfirmModal({ onConfirm, onCancel }) {
  const theme = useTheme();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-sm rounded-2xl p-6 shadow-xl"
        style={{ backgroundColor: theme.card }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
              <Tag size={18} color="white" />
            </div>
            <p className="text-sm font-semibold" style={{ color: theme.text }}>Issue a Voucher Code</p>
          </div>
          <button onClick={onCancel} className="hover:opacity-70 transition-opacity"
            style={{ color: theme.textMuted }}>
            <X size={18} />
          </button>
        </div>
        <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
          This will issue one unique code from your batch. You'll have <strong style={{ color: theme.text }}>5 minutes</strong> to recall it if issued by mistake.
        </p>
        <div className="flex gap-3">
          <button onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-button"
            style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
            Issue Code
          </button>
          <button onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-opacity hover:opacity-75"
            style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Issued code display with copy + recall
function IssuedCode({ code, issuedAt, onRecall }) {
  const theme = useTheme();
  const [copied,    setCopied]    = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [recalled,  setRecalled]  = useState(false);
  const [recallError, setRecallError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(RECALL_WINDOW);
  const timerRef = useRef(null);

  // Countdown
  useEffect(() => {
    const elapsed = Math.floor((Date.now() - new Date(issuedAt).getTime()) / 1000);
    const remaining = Math.max(RECALL_WINDOW - elapsed, 0);
    setSecondsLeft(remaining);

    if (remaining > 0) {
      timerRef.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) { clearInterval(timerRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [issuedAt]);

  function copy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }

  async function handleRecall() {
    setRecalling(true);
    setRecallError('');
    const res = await dealerApi.recallCode(code);
    setRecalling(false);
    if (res.success) {
      setRecalled(true);
      // Small delay so user sees the state change before it disappears
      setTimeout(() => onRecall(code), 800);
    } else {
      setRecallError(res.error || 'Recall failed');
    }
  }

  if (recalled) return (
    <div className="rounded-2xl p-4 flex items-center gap-3"
      style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
      <CheckCircle size={16} style={{ color: '#16a34a' }} />
      <span className="text-sm" style={{ color: theme.textMuted }}>Code <span className="font-mono font-bold" style={{ color: theme.text }}>{code}</span> recalled and returned to pool.</span>
    </div>
  );

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const canRecall = secondsLeft > 0;

  return (
    <div className="rounded-2xl p-5 space-y-3"
      style={{
        backgroundColor: theme.card,
        boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)',
      }}>
      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
        Code issued — give to customer
      </p>

      {/* Code + copy */}
      <div className="flex items-center gap-3 p-4 rounded-xl" style={{ backgroundColor: theme.background }}>
        <span className="font-mono text-xl font-bold tracking-widest flex-1" style={{ color: theme.text }}>
          {code}
        </span>
        <button onClick={copy}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all shrink-0"
          style={{
            backgroundColor: copied ? '#dcfce7' : '#00ACC1',
            color: copied ? '#166534' : '#fff',
          }}>
          {copied ? <><Check size={14} />Copied!</> : <><Copy size={14} />Copy</>}
        </button>
      </div>

      <p className="text-xs" style={{ color: theme.textMuted }}>
        Customer enters this in TripData app → Settings → Upgrade
      </p>

      {recallError && (
        <p className="text-xs" style={{ color: '#f87171' }}>{recallError}</p>
      )}

      {/* Recall section */}
      <div className="flex items-center justify-between pt-1"
        style={{ borderTop: `1px solid ${theme.border}` }}>
        <div className="flex items-center gap-2">
          {canRecall ? (
            <>
              <Clock size={13} style={{ color: theme.textMuted }} />
              <span className="text-xs" style={{ color: theme.textMuted }}>
                Recall available for {mins}:{String(secs).padStart(2, '0')}
              </span>
            </>
          ) : (
            <>
              <AlertTriangle size={13} style={{ color: theme.textMuted }} />
              <span className="text-xs" style={{ color: theme.textMuted }}>Recall window expired</span>
            </>
          )}
        </div>
        {canRecall && (
          <button onClick={handleRecall} disabled={recalling}
            className="text-xs font-medium px-3 py-1.5 rounded-lg transition-opacity hover:opacity-75 disabled:opacity-50"
            style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
            {recalling ? 'Recalling...' : 'Recall Code'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Dealers() {
  const theme    = useTheme();
  const { user } = useAuth();

  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [issuing,      setIssuing]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [issuedCodes,  setIssuedCodes]  = useState([]); // {code, issuedAt}
  const [issueError,   setIssueError]   = useState('');
  const [vouchers,     setVouchers]     = useState([]);
  const [vLoading,     setVLoading]     = useState(false);
  const [filter,       setFilter]       = useState('issued');
  const [tab,          setTab]          = useState('dashboard');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await dealerApi.dashboard();
    setLoading(false);
    if (res.success) setData(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function loadVouchers(f = filter) {
    setVLoading(true);
    const res = await dealerApi.listVouchers({ filter: f, page: 1 });
    setVLoading(false);
    if (res.success) setVouchers(res.data || []);
  }

  useEffect(() => {
    if (tab === 'vouchers') loadVouchers();
  }, [tab, filter]);

  async function handleConfirmIssue() {
    setShowConfirm(false);
    setIssueError('');
    setIssuing(true);
    const res = await dealerApi.issueCode();
    setIssuing(false);
    if (res.success) {
      setIssuedCodes(prev => [{ code: res.data.code, issuedAt: new Date().toISOString() }, ...prev]);
      load();
    } else {
      setIssueError(res.error || 'Failed to issue code');
    }
  }

  function handleRecalled(code) {
    setIssuedCodes(prev => prev.filter(c => c.code !== code));
    load();
  }

  const stats = data?.stats;
  const batch = data?.batch;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-7 h-7 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00ACC1' }} />
    </div>
  );

  if (!data) return (
    <div className="text-center py-12 text-sm" style={{ color: theme.textMuted }}>
      No dealer batch associated with your account.
    </div>
  );

  return (
    <div className="space-y-5 pb-10">

      {/* Confirm modal */}
      {showConfirm && (
        <ConfirmModal
          onConfirm={handleConfirmIssue}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Centered pill header */}
      <div className="flex items-center justify-between">
        <div className="flex-1" />
        <span className="text-sm font-semibold px-5 py-2 rounded-full text-white"
          style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
          {batch?.name || 'Business Portal'}
        </span>
        <div className="flex-1 flex justify-end">
          <button onClick={load} className="p-2 rounded-xl transition-opacity hover:opacity-70"
            style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
            <RefreshCw size={16} style={{ color: theme.textMuted }} />
          </button>
        </div>
      </div>

      <p className="text-center text-xs -mt-2" style={{ color: theme.textMuted }}>
        {batch?.pro_months} month Pro per redemption
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={Tag}         label="Total"     value={stats?.total_codes     || 0} color={theme.text} />
        <StatCard icon={Clock}       label="Available" value={stats?.available_codes || 0} color="#00ACC1" />
        <StatCard icon={RefreshCw}   label="Issued"    value={stats?.issued_codes    || 0} color="#f59e0b" />
        <StatCard icon={CheckCircle} label="Redeemed"  value={stats?.used_codes      || 0} color="#4CAF50" />
      </div>

      {/* Issue button */}
      <div className="flex flex-col items-center gap-3">
        {issueError && (
          <p className="text-sm" style={{ color: '#f87171' }}>{issueError}</p>
        )}
        <button
          onClick={() => setShowConfirm(true)}
          disabled={issuing || stats?.available_codes === 0}
          className="flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60 hover:shadow-button"
          style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
          <Tag size={16} />
          {issuing ? 'Issuing...' : 'Issue Next Code'}
        </button>
        {stats?.available_codes === 0 && (
          <p className="text-xs" style={{ color: '#f87171' }}>
            No codes available. Contact TripData to top up your batch.
          </p>
        )}
      </div>

      {/* Recently issued codes with recall */}
      {issuedCodes.length > 0 && (
        <div className="space-y-3">
          {issuedCodes.map((item, i) => (
            <IssuedCode
              key={item.code}
              code={item.code}
              issuedAt={item.issuedAt}
              onRecall={handleRecalled}
            />
          ))}
        </div>
      )}

      {/* Tabs */}
      <div>
        <div className="flex rounded-xl p-1 mb-4"
          style={{
            backgroundColor: theme.card,
            boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)',
          }}>
          {[
            { id: 'dashboard', label: 'Recent Activity' },
            { id: 'vouchers',  label: 'All Codes' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                backgroundColor: tab === t.id ? '#00ACC1' : 'transparent',
                color: tab === t.id ? '#fff' : theme.textSecondary,
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'dashboard' ? (
          <div className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: theme.card,
              boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)',
            }}>
            {!data.recent_issued?.length ? (
              <p className="text-center py-8 text-sm" style={{ color: theme.textMuted }}>No codes issued yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                      {['Code', 'Issued', 'Issued By', 'Status', 'Redeemed By'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                          style={{ color: theme.textMuted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_issued.map((v, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td className="px-5 py-3 font-mono text-xs font-bold" style={{ color: '#00ACC1' }}>{v.code}</td>
                        <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                          {new Date(v.issued_at).toLocaleDateString('en-ZA')}
                        </td>
                        <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                          {v.issued_by_name || v.issued_by_email || '—'}
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                            style={{
                              backgroundColor: v.used_by_email ? '#dcfce7' : '#fef3c7',
                              color: v.used_by_email ? '#166534' : '#92400e',
                            }}>
                            {v.used_by_email ? 'Redeemed' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                          {v.used_by_email || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-4">
              {[
                { val: 'issued',    label: 'Issued' },
                { val: 'used',      label: 'Redeemed' },
                { val: 'available', label: 'Available' },
              ].map(f => (
                <button key={f.val} onClick={() => { setFilter(f.val); loadVouchers(f.val); }}
                  className="px-4 py-2 rounded-xl text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: filter === f.val ? '#00ACC1' : theme.card,
                    color: filter === f.val ? '#fff' : theme.textSecondary,
                    boxShadow: filter !== f.val ? (theme.isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 1px 4px rgba(0,0,0,0.06)') : 'none',
                  }}>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="rounded-2xl overflow-hidden"
              style={{
                backgroundColor: theme.card,
                boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)',
              }}>
              {vLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="w-5 h-5 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00ACC1' }} />
                </div>
              ) : vouchers.length === 0 ? (
                <p className="text-center py-8 text-sm" style={{ color: theme.textMuted }}>No codes in this category</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                        {['Code', 'Issued', 'Redeemed', 'Expires'].map(h => (
                          <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                            style={{ color: theme.textMuted }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {vouchers.map((v, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                          <td className="px-5 py-3 font-mono text-xs font-bold" style={{ color: '#00ACC1' }}>{v.code}</td>
                          <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                            {v.issued_at ? new Date(v.issued_at).toLocaleDateString('en-ZA') : '—'}
                          </td>
                          <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                            {v.used_at ? new Date(v.used_at).toLocaleDateString('en-ZA') : '—'}
                          </td>
                          <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                            {v.pro_expires_at ? new Date(v.pro_expires_at).toLocaleDateString('en-ZA') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}